import logo from "../../logo.svg";

/**
 * Returns the base API URL (excluding trailing /api).
 * Automatically points to https://posapi.aamardokan.online in production.
 */
export const getApiBase = () => {
  if (typeof window !== "undefined" && window.location) {
    const hostname = window.location.hostname;
    if (
      hostname.includes("aamardokan.online") ||
      hostname.includes("auroratec.net")
    ) {
      return "https://posapi.aamardokan.online";
    }
  }

  const envUrl =
    process.env.REACT_APP_API_URL || "http://localhost:5001/api/";
  let base = envUrl.replace(/\/api\/?$/, "");

  if (
    typeof window !== "undefined" &&
    window.location &&
    window.location.protocol === "https:" &&
    !base.includes("localhost") &&
    !base.includes("127.0.0.1") &&
    base.startsWith("http://")
  ) {
    base = base.replace("http://", "https://");
  }

  return base;
};

/**
 * Returns the full /api endpoint URL.
 */
export const getApiUrl = () => {
  return `${getApiBase()}/api/`;
};

/**
 * Resolves any photo path or URL into a usable image src.
 * Handles:
 * - null / undefined / empty -> returns fallback (or default logo)
 * - blob: and data: URLs (for instant crop previews)
 * - /uploads/... subpaths -> routes via active API server (posapi.aamardokan.online)
 * - Broken historical hosts like aamardokan.online:5001 or api.aamardokan.online -> rewrites to posapi.aamardokan.online
 * - Localhost URLs when running in production -> rewrites to production API base
 * - Relative paths (/uploads/... or uploads/...) -> prepends API base
 */
export const resolvePhotoUrl = (photoPath, fallback = logo) => {
  if (
    !photoPath ||
    typeof photoPath !== "string" ||
    photoPath.trim() === "" ||
    photoPath === "undefined" ||
    photoPath === "null"
  ) {
    return fallback;
  }

  const trimmed = photoPath.trim();

  // Blob or Data URLs (used for instant preview during crop/upload)
  if (trimmed.startsWith("blob:") || trimmed.startsWith("data:")) {
    return trimmed;
  }

  const apiBase = getApiBase();

  const isClientLocal =
    typeof window !== "undefined" &&
    window.location &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  // 1. If URL contains an "/uploads/" subpath (e.g. from local server, broken port, or relative path)
  if (trimmed.includes("/uploads/")) {
    const sub = trimmed.split("/uploads/")[1];
    return `${apiBase}/uploads/${sub}`;
  }

  // 2. If it starts with "uploads/"
  if (trimmed.startsWith("uploads/")) {
    const sub = trimmed.replace(/^uploads\//, "");
    return `${apiBase}/uploads/${sub}`;
  }

  // 3. Full URLs
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    // Rewrite invalid historical ports or uncertified domains
    if (
      trimmed.includes("aamardokan.online:5001") ||
      trimmed.includes("api.aamardokan.online")
    ) {
      return trimmed
        .replace(/https?:\/\/aamardokan\.online:5001/, "https://posapi.aamardokan.online")
        .replace(/https?:\/\/api\.aamardokan\.online/, "https://posapi.aamardokan.online");
    }

    // If stored URL points to localhost but the client is running on production
    if (!isClientLocal && trimmed.includes("localhost")) {
      return trimmed.replace(/https?:\/\/localhost(:\d+)?/, apiBase);
    }

    // Mixed content safeguard
    if (
      typeof window !== "undefined" &&
      window.location &&
      window.location.protocol === "https:" &&
      trimmed.startsWith("http://") &&
      !trimmed.includes("localhost") &&
      !trimmed.includes("127.0.0.1")
    ) {
      return trimmed.replace("http://", "https://");
    }

    return trimmed;
  }

  // 4. Any other relative path
  let cleanPath = trimmed;
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.substring(1);
  }

  return `${apiBase}/uploads/${cleanPath}`;
};

export default resolvePhotoUrl;

